/**
 * A person's preferences: how dates read, what a chart shows by default,
 * which cards their dashboard has and which columns each table keeps.
 *
 * One document per person, stored whole — on the hub per account
 * (`GET/PUT /api/preferences`, Postgres) and in the desktop app per machine
 * (the same endpoint, DuckDB). Both servers run every incoming document through
 * `normalizeUserPrefs`, so a stale client, a hand-edited request or a field a
 * newer build added can never store something the UI then trips over: unknown
 * keys are dropped, bad values fall back to the default.
 *
 * Alerts and push notifications are *not* here: they are read by the server
 * that sends them, and keep their own store (`@strat/notify`).
 */
export const DATE_FORMATS = ["dmy", "mdy", "iso"];
export const TIME_ZONES = ["local", "utc"];
/** Opening chart window: `auto` is the last few hundred bars, whatever the timeframe. */
export const CHART_RANGES = ["auto", "1d", "1w", "1m", "3m", "6m", "1y", "all"];
export const DEFAULT_USER_PREFS = {
    display: { theme: null, dateFormat: "dmy", timeZone: "local" },
    chart: {
        layers: { entries: true, exits: true, tradeLines: true, position: true, stop: true, volume: true },
        range: "auto",
    },
    dashboard: { cards: [] },
    tables: {},
};
/** A stored document's ceiling. Generous for any real set of choices; a wall against abuse. */
export const MAX_USER_PREFS_BYTES = 64 * 1024;
const MAX_TABLES = 40;
const MAX_CARDS = 40;
const MAX_LIST = 60;
const MAX_ID = 64;
const isObject = (v) => typeof v === "object" && v !== null && !Array.isArray(v);
const isId = (v) => typeof v === "string" && v.length > 0 && v.length <= MAX_ID;
function oneOf(values, v, fallback) {
    return typeof v === "string" && values.includes(v) ? v : fallback;
}
function bool(v, fallback) {
    return typeof v === "boolean" ? v : fallback;
}
function normalizeTable(raw) {
    if (!isObject(raw))
        return null;
    const out = {};
    if (Array.isArray(raw.hidden))
        out.hidden = [...new Set(raw.hidden.filter(isId))].slice(0, MAX_LIST);
    if (isObject(raw.sort) && isId(raw.sort.key)) {
        out.sort = { key: raw.sort.key, direction: raw.sort.direction === "asc" ? "asc" : "desc" };
    }
    if (raw.pageSize === 10 || raw.pageSize === 20 || raw.pageSize === 50)
        out.pageSize = raw.pageSize;
    if (isObject(raw.filters)) {
        const filters = {};
        for (const [key, value] of Object.entries(raw.filters).slice(0, MAX_LIST)) {
            if (!isId(key))
                continue;
            if (typeof value === "boolean" || (typeof value === "number" && Number.isFinite(value)))
                filters[key] = value;
            else if (typeof value === "string" && value.length <= 200)
                filters[key] = value;
        }
        out.filters = filters;
    }
    return out;
}
/** Anything in, a complete and valid `UserPrefs` out. Never throws. */
export function normalizeUserPrefs(raw) {
    const d = DEFAULT_USER_PREFS;
    const src = isObject(raw) ? raw : {};
    const display = isObject(src.display) ? src.display : {};
    const chart = isObject(src.chart) ? src.chart : {};
    const layers = isObject(chart.layers) ? chart.layers : {};
    const dashboard = isObject(src.dashboard) ? src.dashboard : {};
    const seen = new Set();
    const cards = [];
    for (const card of Array.isArray(dashboard.cards) ? dashboard.cards : []) {
        if (cards.length >= MAX_CARDS)
            break;
        if (!isObject(card) || !isId(card.id) || seen.has(card.id))
            continue;
        seen.add(card.id);
        cards.push({ id: card.id, visible: bool(card.visible, true) });
    }
    const tables = {};
    if (isObject(src.tables)) {
        for (const [id, table] of Object.entries(src.tables).slice(0, MAX_TABLES)) {
            const t = isId(id) ? normalizeTable(table) : null;
            if (t)
                tables[id] = t;
        }
    }
    return {
        display: {
            theme: display.theme === "light" || display.theme === "dark" ? display.theme : null,
            dateFormat: oneOf(DATE_FORMATS, display.dateFormat, d.display.dateFormat),
            timeZone: oneOf(TIME_ZONES, display.timeZone, d.display.timeZone),
        },
        chart: {
            layers: {
                entries: bool(layers.entries, d.chart.layers.entries),
                exits: bool(layers.exits, d.chart.layers.exits),
                tradeLines: bool(layers.tradeLines, d.chart.layers.tradeLines),
                position: bool(layers.position, d.chart.layers.position),
                stop: bool(layers.stop, d.chart.layers.stop),
                volume: bool(layers.volume, d.chart.layers.volume),
            },
            range: oneOf(CHART_RANGES, chart.range, d.chart.range),
        },
        dashboard: { cards },
        tables,
    };
}
const pad = (n) => String(n).padStart(2, "0");
/**
 * Formats a timestamp the way this person reads dates. The one place the
 * `dateFormat` and `timeZone` choices are applied, so every caller agrees.
 * `compact` writes a two-digit year, for dense tables.
 */
export function formatDateTimePref(ms, display, options = {}) {
    const d = new Date(ms);
    const utc = display.timeZone === "utc";
    const year = utc ? d.getUTCFullYear() : d.getFullYear();
    const month = pad((utc ? d.getUTCMonth() : d.getMonth()) + 1);
    const day = pad(utc ? d.getUTCDate() : d.getDate());
    const y = options.compact ? String(year).slice(2) : String(year);
    const datePart = display.dateFormat === "iso"
        ? `${y}-${month}-${day}`
        : display.dateFormat === "mdy"
            ? `${month}/${day}/${y}`
            : `${day}/${month}/${y}`;
    if (options.time === false)
        return datePart;
    const time = `${pad(utc ? d.getUTCHours() : d.getHours())}:${pad(utc ? d.getUTCMinutes() : d.getMinutes())}`;
    return `${datePart} ${time}${utc ? " UTC" : ""}`;
}
//# sourceMappingURL=prefs.js.map